import { useCallback, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from '../i18n'
import { COLORS } from '../lib/theme'
import { uiLocale } from '../lib/dateFormat'
import { Icon } from './Icon'
import { Avatar } from './atoms'
// Reused rather than reimplemented: the plan-hunt avatar already derives initials
// the same way, and two copies would drift.
import { initialsOf } from './planHunt/PlanHuntAvatar'
import { ReportMap } from './huntReport/ReportMap'
import {
  ReportPanel,
  ReportSection,
  ReportStatBar,
  ReportPin,
  useIsNarrow,
  type ReportStat,
} from './huntReport/reportChrome'
// The game-reports card owns the ended hunt's game area — preview map, counts,
// pin list and the add/edit/remove editor (which it reuses from the shipped
// downed-game tab). One implementation, one set of permission rules.
import { GameReportsCard } from './huntReport/GameReportsCard'
import type { GamePinDraft } from './HuntInvitationModal'
import { countGamePins } from './huntReport/gameReports'
import type { HuntableGameSpecies } from '../lib/huntable-game-catalog'
import type {
  GamePinInput,
  GamePinView,
  GeoArea,
  GeoPt,
  HuntDog,
  HuntPlanParticipant,
} from '../lib/types'

export interface HuntReportPageProps {
  /** May be empty — hunts are commonly created without a name. */
  /** May be null: the API sends no name for a hunt created without one (older
   *  hunts especially). Rendered as the untitled/date fallback, never trimmed raw. */
  huntName: string | null
  teamName: string
  /** ISO 8601. */
  startTime: string
  /**
   * ISO 8601, or null when the hunt never recorded an end. Null is the common
   * case in production, so the duration stat is dropped from the bar rather than
   * computed from the current time.
   */
  endTime: string | null
  participants: HuntPlanParticipant[]
  gamePins: GamePinView[]
  /** The team ground the report map draws under the pins. */
  boundaryAreas?: readonly GeoArea[]
  subAreaPolygons?: readonly GeoArea[]
  forbiddenPolygons?: readonly GeoArea[]
  teamBorderColor?: string
  /** The pins could not be read — see `DownedGameTab.loadFailed`. */
  gamePinsLoadFailed?: boolean
  dogs?: HuntDog[]
  /** The hunt's own note, or null when none was written. */
  comment?: string | null
  /**
   * Total hours in the field, summed server-side across the participants' own
   * windows. Zero is a real answer — a hunt whose roster never closed a window
   * has no derivable time — so the stat shows it rather than hiding.
   */
  manHours?: number
  /** The signed-in user, so their own row can be marked. */
  currentUserId?: string
  /** True when the viewer may edit anyone's game report, not only their own. */
  canManageAll?: boolean
  /** Where a newly added pin is placed when the user does not move it. */
  defaultLocation?: GeoPt
  speciesCatalog?: readonly HuntableGameSpecies[]
  /**
   * Passed straight through to the downed-game editor. Omitting them renders it
   * read-only, which is what a viewer without permission should see.
   */
  onCreateGamePin?: (input: GamePinInput) => Promise<void>
  onUpdateGamePin?: (id: string, input: GamePinInput) => Promise<void>
  onDeleteGamePin?: (id: string) => Promise<void>
  /** Rendered in the header — campfire's "Replay hunt" button arrives here. */
  headerAction?: ReactNode
  /**
   * The "Thank the participants" card, filled by the domain with the send-a-thank-you
   * flow. A slot rather than built in, so @wehunt/ui stays free of the API call; only
   * shown to a sender (the domain omits it for a plain participant, whose GET is 403).
   */
  thankYou?: ReactNode
  /**
   * The "What participants can see" card, filled by the domain with the
   * report-sharing toggle. A slot rather than built in, so @wehunt/ui stays free of
   * the API call; only shown on an estate hunt to a manager (the domain omits it
   * otherwise, since a plain participant's GET is 403). Renders alongside the
   * thank-you area.
   */
  reportSharing?: ReactNode
  onBack?: () => void
}

interface HunterTally {
  userId: string
  name: string
  isLeader: boolean
  downed: number
  /**
   * Hit but not recovered. Its own figure, never folded into the downed count:
   * a wounded animal is an outstanding tracking job, not a harvest, and the two
   * carry different obligations.
   */
  wounded: number
}

/** Animals, not pins: one pin can carry several. */
function animalCount(pin: GamePinView): number {
  return pin.games?.length ?? 0
}

/**
 * Per-hunter tallies, keyed on who is credited with the animal.
 *
 * `shooterId` where it is set, falling back to the reporter: a pin registered by
 * the hunt leader on someone else's behalf belongs to the shooter, and only the
 * reporter is guaranteed to be present.
 */
export function tallyHunters(
  participants: HuntPlanParticipant[],
  gamePins: GamePinView[],
): HunterTally[] {
  const tallies = new Map<string, HunterTally>(
    participants.map((p) => [
      p.userId,
      { userId: p.userId, name: p.userName, isLeader: p.isLeader, downed: 0, wounded: 0 },
    ]),
  )

  for (const pin of gamePins) {
    const creditedTo = pin.shooterId ?? pin.reporterId
    const tally = tallies.get(creditedTo)
    // A pin credited to someone who is not on the participant list is still
    // counted in the hunt totals above, just not attributed to a row here.
    if (!tally) continue
    // Only real downed / wounded pins are attributed here; an observed sighting
    // is neither a harvest nor a tracking job, so it is not folded into either.
    if (pin.pinType === 'downed') tally.downed += animalCount(pin)
    else if (pin.pinType === 'wounded') tally.wounded += animalCount(pin)
  }

  // Leaders first, then by name, so the list reads the same on every load
  // regardless of the order the API returned participants in.
  return [...tallies.values()].sort((a, b) => {
    if (a.isLeader !== b.isLeader) return a.isLeader ? -1 : 1
    return a.name.localeCompare(b.name)
  })
}

/** Whole hours between the two instants, or null when the hunt has no end. */
export function huntDurationHours(startTime: string, endTime: string | null): number | null {
  if (endTime === null) return null
  const start = Date.parse(startTime)
  const end = Date.parse(endTime)
  if (Number.isNaN(start) || Number.isNaN(end) || end <= start) return null
  return Math.round((end - start) / 3_600_000)
}

/**
 * A packed ARGB integer as a CSS colour, or null when there is none.
 *
 * Alpha is dropped deliberately: these colours identify a dog, and a
 * half-transparent swatch would read as a different colour rather than the same
 * one faded.
 */
export function argbToCss(argb: number | null | undefined): string | null {
  if (argb === null || argb === undefined) return null
  const hex = (argb & 0xffffff).toString(16).padStart(6, '0')
  return `#${hex}`
}

export function HuntReportPage({
  huntName,
  teamName,
  startTime,
  endTime,
  participants,
  gamePins,
  boundaryAreas,
  subAreaPolygons,
  forbiddenPolygons,
  teamBorderColor,
  gamePinsLoadFailed = false,
  dogs = [],
  comment = null,
  manHours,
  currentUserId,
  canManageAll = false,
  defaultLocation,
  speciesCatalog = [],
  onCreateGamePin,
  onUpdateGamePin,
  onDeleteGamePin,
  headerAction,
  thankYou,
  reportSharing,
  onBack,
}: HuntReportPageProps) {
  const { t, i18n } = useTranslation('huntInvitation')
  const narrow = useIsNarrow()
  const [selectedPinId, setSelectedPinId] = useState<string | null>(null)

  // The add/edit form (inside GameReportsCard) reports its live position intent
  // here, and the big left map becomes a centre-pin picker for it: `draft` holds
  // the open form's pinType + seed camera, `draftLocation` the picked centre.
  const [draft, setDraft] = useState<GamePinDraft | null>(null)
  const [draftLocation, setDraftLocation] = useState<GeoPt | null>(null)
  // Seed the picked location from the draft's camera only when the form OPENS —
  // not when its pinType changes mid-edit, which must keep the user's pan.
  const draftOpenRef = useRef(false)
  const handleDraftChange = useCallback((next: GamePinDraft | null) => {
    const wasOpen = draftOpenRef.current
    draftOpenRef.current = next !== null
    setDraft(next)
    if (next && !wasOpen) setDraftLocation(next.seedView.center)
    else if (!next) setDraftLocation(null)
  }, [])

  // Tallied by the pin's REAL type, so observed / downed / wounded stay distinct.
  // An observed sighting is not a wounded animal: folding it into "wounded" would
  // report a report of game seen as an outstanding tracking job.
  const { observed, downed, wounded } = countGamePins(gamePins)
  const durationHours = huntDurationHours(startTime, endTime)
  const hunters = tallyHunters(participants, gamePins)

  const dateLabel = new Date(startTime).toLocaleDateString(uiLocale(i18n.language), {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
  // An unnamed hunt gets the date rather than an empty heading. Hunts are
  // commonly created without a name, so this is the normal path, not a guard —
  // and the API sends null (not "") for them, so the optional chain is load-bearing:
  // without it the whole report page crashed to "Something went wrong" for any
  // hunt with no name.
  const title = huntName?.trim() || t('report.untitled', { date: dateLabel })

  const stats: ReportStat[] = [
    // Dropped rather than dashed when the hunt recorded no end: a dash would read
    // as "no time in the field", which is a different claim.
    ...(durationHours !== null
      ? [
          {
            icon: <Icon name="clock" size={22} color="#6b6345" filled />,
            value: t('report.hours', { count: durationHours }),
            label: t('report.duration'),
          },
        ]
      : []),
    { icon: <ReportPin kind="observed" />, value: observed, label: t('gameReports.observed') },
    { icon: <ReportPin kind="downed" />, value: downed, label: t('downedGame.typeDowned') },
    { icon: <ReportPin kind="wounded" />, value: wounded, label: t('downedGame.typeWounded') },
    // Man-hours survive a missing hunt end, because they are summed from each
    // participant's own window rather than the hunt's span.
    ...(manHours !== undefined
      ? [
          {
            icon: <Icon name="clock" size={22} color="#EE7C33" filled />,
            value: t('report.hours', { count: manHours }),
            label: t('report.manHours'),
          },
        ]
      : []),
  ]

  const header = (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap' }}>
      <div style={{ flex: 1, minWidth: 180 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, flexWrap: 'wrap' }}>
          <span
            className="wh-chip"
            style={{
              background: COLORS.tertiaryContainer,
              color: COLORS.onTertiaryContainer,
              borderColor: 'transparent',
            }}
          >
            {t('report.endedChip')}
          </span>
        </div>
        {/* An h1 rather than the prototype's div: the visual treatment is the
            same, and the report is a page — a screen reader needs a heading to
            land on. Matching a mock should not cost the document outline. */}
        <h1
          style={{
            fontFamily: COLORS.fontDisplay,
            fontSize: 24,
            fontWeight: 500,
            lineHeight: 1.2,
            margin: 0,
          }}
        >
          {title}
        </h1>
        <div style={{ fontSize: 13, color: COLORS.outline, marginTop: 2 }}>
          {teamName} · {dateLabel}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
        {headerAction}
        {onBack ? (
          <button type="button" className="wh-btn-icon" title={t('report.back')} onClick={onBack}>
            <Icon name="close" size={18} />
          </button>
        ) : null}
      </div>
    </div>
  )

  return (
    <ReportPanel header={header}>
      {/* The prototype is two-pane on wide screens with the map filling the panel
          height. The map pane is not built yet, so the report column takes the
          width on its own — capped, so the rows do not run the whole panel. */}
      <div
        style={
          narrow
            ? {
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                padding: 16,
                display: 'flex',
                flexDirection: 'column',
                gap: 16,
              }
            : {
                flex: 1,
                minHeight: 0,
                overflow: 'hidden',
                padding: 20,
                display: 'flex',
                flexDirection: 'row',
                gap: 20,
              }
        }
      >
        {/* Wide: the map fills the panel height while the report column scrolls
            beside it. Narrow: the map sits on top at a fixed height, because a
            full-height map would push every figure below the fold. */}
        <div
          style={
            narrow
              ? { width: '100%', flexShrink: 0 }
              : { flex: '1 1 420px', minWidth: 0, height: '100%' }
          }
        >
          <ReportMap
            gamePins={gamePins}
            boundaryAreas={boundaryAreas}
            subAreaPolygons={subAreaPolygons}
            forbiddenPolygons={forbiddenPolygons}
            teamBorderColor={teamBorderColor}
            height={narrow ? 300 : '100%'}
            selectedPinId={selectedPinId}
            onSelectPin={setSelectedPinId}
            recenterOnSelect
            draftPinType={draft?.pinType ?? null}
            draftInitialView={draft?.seedView ?? null}
            onDraftCenterChange={setDraftLocation}
          />
        </div>

        <div
          style={
            narrow
              ? { width: '100%' }
              : {
                  flex: '1 1 400px',
                  minWidth: 0,
                  maxWidth: 520,
                  height: '100%',
                  overflowY: 'auto',
                  paddingRight: 6,
                }
          }
        >
          {/* The flex column is a separate element from the scroller above it, and
              must stay that way. When the height-constrained scroller is ITSELF
              the flex column, flex-shrink collapses each section to fit instead of
              letting the column overflow and scroll — the sections render as
              cropped boxes, and a tall one like the add-game form loses everything
              below its first field. The prototype hit this and named it the
              "empty boxes" bug; merging these two divs is how it comes back. */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <ReportStatBar stats={stats} />

        <GameReportsCard
          gamePins={gamePins}
          participants={participants}
          speciesCatalog={speciesCatalog}
          currentUserId={currentUserId}
          canManageAll={canManageAll}
          defaultLocation={defaultLocation}
          gamePinsLoadFailed={gamePinsLoadFailed}
          boundaryAreas={boundaryAreas}
          subAreaPolygons={subAreaPolygons}
          forbiddenPolygons={forbiddenPolygons}
          teamBorderColor={teamBorderColor}
          selectedPinId={selectedPinId}
          onSelectPin={setSelectedPinId}
          onDraftChange={handleDraftChange}
          draftLocation={draftLocation}
          onCreateGamePin={onCreateGamePin}
          onUpdateGamePin={onUpdateGamePin}
          onDeleteGamePin={onDeleteGamePin}
        />

        {reportSharing}

        {thankYou}

        <ReportSection title={`${t('report.hunters')} (${hunters.length})`}>
          {hunters.length === 0 ? (
            <div style={{ fontSize: 13, color: COLORS.outline }}>{t('report.noHunters')}</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {hunters.map((hunter) => (
                <div
                  key={hunter.userId}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '10px 0',
                    borderTop: `1px solid ${COLORS.outlineVariant}`,
                  }}
                >
                  <Avatar initials={initialsOf(hunter.name)} size={34} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600 }}>
                      {hunter.name}
                      {hunter.userId === currentUserId ? (
                        <span style={{ color: COLORS.outline, fontWeight: 400 }}> {t('report.you')}</span>
                      ) : null}
                    </div>
                    {hunter.isLeader ? (
                      <div style={{ fontSize: 12, color: COLORS.outline }}>{t('overview.huntLeader')}</div>
                    ) : null}
                  </div>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontWeight: 700 }}>
                    <ReportPin kind="downed" size={20} /> {hunter.downed}
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontWeight: 700 }}>
                    <ReportPin kind="wounded" size={20} /> {hunter.wounded}
                  </span>
                </div>
              ))}
            </div>
          )}
        </ReportSection>

        {/* Omitted rather than shown empty: a hunt with no dogs has nothing to
            say, and an empty section reads as data that failed to load. */}
        {dogs.length > 0 ? (
          <ReportSection title={`${t('report.dogs')} (${dogs.length})`}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {dogs.map((dog) => (
                <div
                  key={dog.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '10px 0',
                    borderTop: `1px solid ${COLORS.outlineVariant}`,
                  }}
                >
                  <span
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: '50%',
                      // The dog's own colour, the same one its track carries on
                      // the map, so a row can be tied to a trail.
                      background: argbToCss(dog.colorArgb) ?? COLORS.secondaryContainer,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Icon
                      name="dog"
                      size={18}
                      color={dog.colorArgb === null || dog.colorArgb === undefined ? COLORS.onSecondaryContainer : '#fff'}
                    />
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600 }}>{(dog.name ?? '').trim() || t('report.unnamedDog')}</div>
                    {dog.ownerName ? (
                      <div style={{ fontSize: 12, color: COLORS.outline }}>{dog.ownerName}</div>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          </ReportSection>
        ) : null}

        {comment !== null && comment.trim() !== '' ? (
          <ReportSection title={t('report.comment')}>
            {/* Preserves the line breaks the author typed; a hunt note is often a
                short list rather than a paragraph. */}
            <p style={{ margin: 0, lineHeight: 1.5, color: COLORS.onSurface, whiteSpace: 'pre-wrap' }}>
              {comment}
            </p>
          </ReportSection>
        ) : null}
          </div>
        </div>
      </div>
    </ReportPanel>
  )
}
